import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { RemoteFlagName } from '../../../constants/features';
import {
    getFeatureFlagStatuses,
    ListFeatureFlagStatusResponse,
} from '../../../data/services/discovery-service/get-feature-flags';
import { ClientError, FailureOperation, isSuccessResponse } from '../../../models/common';
import { RemoteFlagsSet } from '../../../models/features';
import { trackTrace } from '../../../utilities/telemetry/channel';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import {
    initializeRemoteFlags,
    initializeRemoteFlagsError,
    initializeRemoteFlagsFailed,
    initializeRemoteFlagsSuccess,
} from '../../actions/remote-flags/remote-flags-action-creators';
import { InitializeRemoteFlagsAction } from '../../actions/remote-flags/remote-flags-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeLeading } from '../../effects/take';
import { getTenantId } from '../../selector/identity-selectors';

export function* initializeRemoteFlagsSaga(action: InitializeRemoteFlagsAction): SagaIterator {
    const { meta } = action;
    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        const tenantId: string = yield select(getTenantId);

        const remoteFeatureFlags = RemoteFlagsSet();
        const flags = Object.keys(remoteFeatureFlags);

        const response: ListFeatureFlagStatusResponse = yield call(
            getFeatureFlagStatuses,
            accessToken,
            tenantId,
            flags
        );

        if (isSuccessResponse(response)) {
            const remoteFeatureFlagsResponse = response.data;
            let flagString = '';

            for (const flag in remoteFeatureFlagsResponse) {
                if (remoteFeatureFlagsResponse[flag]) {
                    remoteFeatureFlags[flag as RemoteFlagName] = true;
                    flagString = flagString.concat(flag, ', ');
                }
            }
            trackTrace(`Initialized remote flags. Enabled flags: ${flagString}`);

            yield put(initializeRemoteFlagsSuccess({ result: remoteFeatureFlags }));
            yield resolveAction(action, { data: remoteFeatureFlags, succeeded: true });
        } else {
            yield put(initializeRemoteFlagsFailed({ failure: response }, meta));
            yield resolveAction(action, response);
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.FetchRemoteFlags);
        yield put(initializeRemoteFlagsError({ error }));
        yield rejectAction(action, error);
    }
}

export function* initializeRemoteFlagsListenerSaga(): SagaIterator {
    yield takeLeading(initializeRemoteFlags, initializeRemoteFlagsSaga);
}
